"use client"
import Card from '@/components/ui/Card'
import Image from 'next/image'
import React from 'react'
import { Accordion, AccordionTab } from 'primereact/accordion';
import PageBanner from '@/components/ui/PageBanner';
import Head from 'next/head';
import DownloadApp from '@/components/shared/DownloadApp';
import Link from 'next/link'

export default function AboutPage() {
    const title = 'About Us | CarSparesEzyFind.co.za - Your Premier Auto Parts Platform'
    const description = 'Discover how CarSparesEzyFind.co.za connects you with quality car parts, scrapyard components, and automotive services across South Africa. Find, compare, and purchase with ease.'
    const keywords = 'car parts, scrapyard parts, auto parts, automotive services, car spares, vehicle parts, CarSparesEzyFind'

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="title" content={title} />
                <meta name='description' content={description} />
                <meta name='keywords' content={keywords} />
            </Head>
            <PageBanner
                backgroundImage='/images/Car Spares-about.webp'
                title='About CarSparesEzyFind'
            />
            
            {/* How It Works Section */}
            <section className="container py-16">
                <div className="text-center mb-12">
                    <h1 className='text-4xl lg:text-5xl font-bold text-gray-800 mb-4'>
                        How CarSparesEzyFind Works
                    </h1>
                    <p className='text-lg text-gray-600 max-w-3xl mx-auto'>
                        Your one-stop platform for car parts, scrapyard components, and automotive services. 
                        Connect with trusted suppliers and get the best deals across South Africa.
                    </p>
                </div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
                <div className='group hover:transform hover:scale-105 transition-all duration-300'>
                    <Card 
                        title="Search & Request"
                        className='text-center h-full border-0 shadow-lg hover:shadow-xl'
                    >
                        <div className='p-6'>
                            <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <Image src={'/images/register.png'} width={40} height={40} alt='search' className='' />
                            </div>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Search & Request</h3>
                            <p className='text-gray-600'>Browse our extensive catalog or upload photos of the parts you need. Specify your location and requirements.</p>
                        </div>
                    </Card>
                </div>

                <div className='group hover:transform hover:scale-105 transition-all duration-300'>
                    <Card 
                        title="Connect & Compare"
                        className='text-center h-full border-0 shadow-lg hover:shadow-xl'
                    >
                        <div className='p-6'>
                            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <Image src={'/images/enquire.png'} width={40} height={40} alt='connect' className='' />
                            </div>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Connect & Compare</h3>
                            <p className='text-gray-600'>Get connected with verified suppliers, scrapyards, and service providers in your area.</p>
                        </div>
                    </Card>
                </div>

                <div className='group hover:transform hover:scale-105 transition-all duration-300'>
                    <Card 
                        title="Receive Quotes"
                        className='text-center h-full border-0 shadow-lg hover:shadow-xl'
                    >
                        <div className='p-6'>
                            <div className='w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <Image src={'/images/advice.png'} width={40} height={40} alt='quotes' className='' />
                            </div>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Receive Quotes</h3>
                            <p className='text-gray-600'>Get competitive quotes from multiple suppliers with pricing, availability, and delivery options.</p>
                        </div>
                    </Card>
                </div>

                <div className='group hover:transform hover:scale-105 transition-all duration-300'>
                    <Card 
                        title="Purchase & Collect"
                        className='text-center h-full border-0 shadow-lg hover:shadow-xl'
                    >
                        <div className='p-6'>
                            <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <Image src={'/images/confirm.png'} width={40} height={40} alt='purchase' className='' />
                            </div>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Purchase & Collect</h3>
                            <p className='text-gray-600'>Choose the best deal and arrange delivery or collection. Track your order until completion.</p>
                        </div>
                    </Card>
                </div>
            </div>

            {/* About Us Section */}
            <section className="bg-gray-50 py-16">
                <div className="container">
                    <div className='flex flex-col lg:flex-row items-center gap-12'>
                        <div className='lg:w-1/2'>
                            <div className='relative'>
                                <div className='absolute -top-6 -left-6 w-full h-full border-4 border-blue-500 -z-10'></div>
                                <Image 
                                    src={'/images/Car-Spares-EzyFind.webp'} 
                                    className='w-full h-auto object-cover rounded-lg shadow-lg' 
                                    height={500} 
                                    width={500} 
                                    alt='CarSparesEzyFind Platform' 
                                />
                            </div>
                        </div>
                        <div className="lg:w-1/2">
                            <h2 className='text-4xl font-bold text-gray-800 mb-4'>CarSparesEzyFind.co.za</h2>
                            <h3 className='text-2xl text-blue-600 font-semibold mb-6'>South Africa's Premier Auto Parts Marketplace</h3>
                            
                            <div className='space-y-4 text-gray-700'>
                                <p>CarSparesEzyFind.co.za is South Africa's leading digital marketplace for automotive parts and services. We connect car owners, mechanics, and businesses with a vast network of suppliers, scrapyards, and service providers nationwide.</p>
                                
                                <p>Whether you need new OEM parts, quality used components from scrapyards, or specialized automotive services, our platform makes finding and purchasing auto parts simple, fast, and reliable.</p>
                                
                                <p className='font-semibold text-blue-600'>From engine components to body panels, electrical parts to performance upgrades - find everything automotive in one convenient location!</p>
                            </div>

                            <div className='mt-8 grid grid-cols-2 gap-4'>
                                <div className='text-center p-4 bg-white rounded-lg shadow'>
                                    <h4 className='text-2xl font-bold text-blue-600'>10,000+</h4>
                                    <p className='text-gray-600'>Auto Parts Listed</p>
                                </div>
                                <div className='text-center p-4 bg-white rounded-lg shadow'>
                                    <h4 className='text-2xl font-bold text-blue-600'>500+</h4>
                                    <p className='text-gray-600'>Verified Suppliers</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16">
                <div className="container">
                    <div className="text-center mb-12">
                        <h2 className='text-4xl font-bold text-gray-800 mb-4'>Why Choose CarSparesEzyFind?</h2>
                        <p className='text-lg text-gray-600'>Experience the future of automotive parts shopping</p>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
                        <div className='bg-white p-6 rounded-lg shadow-lg border-l-4 border-blue-500'>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Extensive Parts Catalog</h3>
                            <p className='text-gray-600'>Access thousands of new and used auto parts from OEM components to scrapyard finds for the south african market. Search by vehicle make, model, year, part number, or VIN number.</p>
                        </div>

                        <div className='bg-white p-6 rounded-lg shadow-lg border-l-4 border-green-500'>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Verified Suppliers Network</h3>
                            <p className='text-gray-600'>All spare shop suppliers, Queue for you services and scrap yards are verified for quality and reliability. Read reviews and ratings from other customers.</p>
                        </div>

                        <div className='bg-white p-6 rounded-lg shadow-lg border-l-4 border-yellow-500'>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Competitive Pricing</h3>
                            <p className='text-gray-600'>Compare prices from multiple suppliers and scrapyards. Get the best deals on quality parts with transparent pricing.</p>
                        </div>

                        <div className='bg-white p-6 rounded-lg shadow-lg border-l-4 border-red-500'>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Complete Auto Services</h3>
                            <p className='text-gray-600'>Beyond parts - find mechanics, auto electricians, panel beaters, roadside assistance and Queue For You services.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mobile App Section */}
            <section className="bg-gradient-to-r from-blue-600 to-blue-800 py-16 text-white">
                <div className="container">
                    <div className='flex flex-col lg:flex-row items-center gap-12'>
                        <div className="lg:w-1/2">
                            <h2 className='text-4xl font-bold mb-6'>Take CarSparesEzyFind With You</h2>
                            <h3 className='text-xl mb-4'>Download Our Mobile App</h3>
                            
                            <div className='space-y-4'>
                                <p>Search for parts on the go, receive instant notifications for new quotes, and manage your orders from anywhere in South Africa.</p>
                                <p>Available for iPhone, Android, and Huawei devices with offline browsing capabilities.</p>
                            </div>

                            <div className='mt-8 flex flex-wrap gap-4'>
                                <Link href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793" target="_blank" rel="noopener noreferrer">
                                    <div className='bg-white text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer'>
                                        📱 iOS App Store
                                    </div>
                                </Link>
                                <Link href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1" target="_blank" rel="noopener noreferrer">
                                    <div className='bg-white text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer'>
                                        🤖 Google Play
                                    </div>
                                </Link>
                                <Link href="https://appgallery.huawei.com/app/C107696919" target="_blank" rel="noopener noreferrer">
                                    <div className='bg-white text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer'>
                                        📲 Huawei AppGallery
                                    </div>
                                </Link>
                            </div>
                        </div>

                        <div className='lg:w-1/2'>
                            <div className='relative'>
                                <Image 
                                    src={'/images/Car-Spares-EzyFind.webp'} 
                                    className='w-full h-auto object-cover rounded-lg' 
                                    height={400} 
                                    width={400} 
                                    alt='Mobile App' 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Business Benefits Section */}
            <section className="py-16 bg-gray-50">
                <div className="container">
                    <div className="text-center mb-12">
                        <h2 className='text-4xl font-bold text-gray-800 mb-4'>Grow Your Auto Business</h2>
                        <p className='text-lg text-gray-600'>Join thousands of suppliers and service providers already on our platform</p>
                    </div>

                    <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                        <div className='text-center'>
                            <div className='w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <span className='text-3xl'>🏪</span>
                            </div>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>List Your Inventory</h3>
                            <p className='text-gray-600'>Upload your parts catalog and reach customers across South Africa. Perfect for scrapyards and parts suppliers.</p>
                        </div>

                        <div className='text-center'>
                            <div className='w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <span className='text-3xl'>📈</span>
                            </div>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Increase Sales</h3>
                            <p className='text-gray-600'>Connect with more customers and increase your sales through our digital marketplace platform.</p>
                        </div>

                        <div className='text-center'>
                            <div className='w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <span className='text-3xl'>🚀</span>
                            </div>
                            <h3 className='text-xl font-bold text-gray-800 mb-3'>Streamline Operations</h3>
                            <p className='text-gray-600'>Manage orders, track inventory, and communicate with customers all from one dashboard.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16">
                <div className="container">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">Frequently Asked Questions</h2>
                        
                        <div className='bg-white rounded-lg shadow-lg overflow-hidden'>
                            <Accordion activeIndex={0}>                           
                                <AccordionTab
                                    header="What types of auto parts can I find on CarSparesEzyFind?"
                                    headerClassName='p-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100'
                                >
                                    <div className='p-4'>
                                        <p>You can find new OEM parts, quality used parts from scrapyards, aftermarket components, performance parts, and accessories for all vehicle makes and models. From engine parts to body panels, electrical components to interior accessories.</p>
                                    </div>
                                </AccordionTab>
                                
                                <AccordionTab
                                    header="How do I know if a supplier is reliable?"
                                    headerClassName='p-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100'
                                >
                                    <div className='p-4'>
                                        <p>All suppliers on CarSparesEzyFind are verified through our screening process. You can view supplier ratings, read customer reviews, and check their business credentials before making a purchase.</p>
                                    </div>
                                </AccordionTab>
                                
                                <AccordionTab
                                    header="Can I negotiate prices with suppliers?"
                                    headerClassName='p-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100'
                                >
                                    <div className='p-4'>
                                        <p>Yes! Our platform includes live chat functionality where you can communicate directly with suppliers to negotiate prices, discuss bulk discounts, and arrange special terms for your specific needs.</p>
                                    </div>
                                </AccordionTab>

                                <AccordionTab
                                    header="Do you offer delivery services?"
                                    headerClassName='p-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100'
                                >
                                    <div className='p-4'>
                                        <p>Many of our suppliers offer delivery services across South Africa. You can also arrange collection from supplier locations or scrapyards. Delivery options and costs are clearly displayed for each supplier.</p>
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </div>
                    </div>
                </div>
            </section>

            <DownloadApp />
            </section>
        </>
    )
}